<!--
  Shared soft look of all autocompletes: a thin wrapper around v-autocomplete that keeps every prop, event and slot
  (everything not declared here is passed through via $attrs / $listeners). It only changes the presentation:
  - multiple: removable chips with a "+n" overflow instead of comma text
  - selected items first (pinned when the menu opens, so rows don't jump while toggling) with a subtle check,
    no coloured "active" highlight
  - rows with icon/avatar + label + context line (AcItem / EntityPickerItem)
  - optional group headers (group-by), a small hint only while the field is empty and suggestions are shown
  - empty state "Keine Treffer für …", loading skeleton and an optional "Neu anlegen" row (create-text + @create)
  Values, filters, searches and emitted events are those of v-autocomplete / the wrapping component.
-->
<template>
  <v-autocomplete ref="ac" :class="rootClass" :clearable="computedClearable" :disabled="disabled" :item-text="itemText"
                  :item-value="itemValue" :items="displayItems" :loading="loading" :menu-props="computedMenuProps"
                  :multiple="multiple" :search-input="searchInput" :value="value" item-color="ac" v-bind="$attrs"
                  v-on="$listeners" @update:search-input="onSearch">
    <!-- Slots of the wrapping component are passed through unchanged -->
    <template v-for="name in passedSlotNames()" v-slot:[name]>
      <slot :name="name"/>
    </template>
    <template v-for="name in passedScopedSlotNames()" v-slot:[name]="scope">
      <slot :name="name" v-bind="scope"/>
    </template>

    <template v-if="!hasSlot('item')" v-slot:item="{ item, attrs }">
      <ac-item :entity="itemEntity" :icon="itemIcon" :item="item" :selected="!!attrs.inputValue" :text-key="itemText"/>
    </template>

    <template v-if="multiple && !hasSlot('selection')" v-slot:selection="{ item, index, parent }">
      <ac-chip v-if="chipAt(index, parent).chip" :disabled="disabled || isReadonly" :text="textOf(item)"
               :color="chipColor(item)" @remove="parent.selectItem(item)"/>
      <span v-else-if="chipAt(index, parent).more" :title="hiddenTitle(parent)" class="ac-more">
        +{{ chipAt(index, parent).more }}
      </span>
    </template>

    <template v-if="!hasSlot('prepend-item')" v-slot:prepend-item>
      <div v-if="showHint" class="ac-hint">
        <v-icon x-small>mdi-lightbulb-outline</v-icon>
        {{ suggestionHint }}
      </div>
    </template>

    <template v-if="!hasSlot('no-data')" v-slot:no-data>
      <div class="ac-empty" @mousedown.prevent>
        <template v-if="empty.kind === 'loading'">
          <div v-for="n in 3" :key="n" class="ac-skeleton">
            <span class="ac-skeleton__avatar"></span>
            <span class="ac-skeleton__lines">
              <span class="ac-skeleton__line"></span>
              <span class="ac-skeleton__line ac-skeleton__line--short"></span>
            </span>
          </div>
        </template>
        <template v-else>
          <div class="ac-empty__title">
            <v-icon small>{{ empty.kind === 'no-match' ? 'mdi-magnify-close' : 'mdi-magnify' }}</v-icon>
            {{ empty.title }}
          </div>
          <div v-if="empty.text" class="ac-empty__text">{{ empty.text }}</div>
        </template>
        <button v-if="createText && empty.kind !== 'loading'" class="ac-create" type="button"
                @click.stop="$emit('create', currentSearch || '')">
          <v-icon small>mdi-plus</v-icon>
          {{ createText }}
        </button>
      </div>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import Vue from 'vue';
import AcItem from './AcItem.vue';
import AcChip from './AcChip.vue';
import {
  buildMenuItems,
  chipSlot,
  cssColor,
  ChipSlot,
  DEFAULT_MAX_CHIPS,
  emptyState,
  EmptyState,
  GroupOf,
  hiddenSelectionTitle,
  itemKey,
  mergeMenuProps,
  selectedKeys,
  showSuggestionHint,
} from './autocomplete.helper';

/** Boolean attribute passed through $attrs ("" for a bare attribute, true, or any other value except false) */
function isOn(attrs: Record<string, unknown> | undefined, name: string): boolean {
  if (!attrs || !(name in attrs)) {
    return false;
  }
  const value = attrs[name];
  return value !== false && value !== undefined && value !== null;
}

interface SelectParent {
  selectedItems: unknown[];
  getText(item: unknown): unknown;
}

export default Vue.extend({
  name: 'SoftAutocomplete',
  components: {AcItem, AcChip},
  inheritAttrs: false,
  props: {
    items: {type: Array as () => unknown[], default: () => []},
    value: {required: false},
    multiple: {type: Boolean, default: false},
    loading: {type: [Boolean, String], default: false},
    disabled: {type: Boolean, default: false},
    searchInput: {type: String, default: undefined},
    menuProps: {type: [Object, String], default: undefined},
    /** Single fields: as passed. Multiple fields always offer "alle entfernen" (same as removing every chip) */
    clearable: {type: Boolean, default: false},
    /** Text of the empty menu while nothing is typed */
    noDataText: {type: String, default: ''},
    itemText: {type: String, default: 'text'},
    itemValue: {type: String, default: 'value'},
    /** Group label per item (e.g. "Bewerber:innen"); headers only when it separates something */
    groupBy: {type: Function as unknown as () => GroupOf<any>, default: undefined},
    groupOrder: {type: Array as () => string[], default: undefined},
    /** Avatar of rows without own entity/icon (presentation only, items and emitted values stay untouched) */
    itemEntity: {type: String, default: undefined},
    itemIcon: {type: String, default: undefined},
    /** Selected items first */
    pinSelected: {type: Boolean, default: true},
    maxChips: {type: Number, default: DEFAULT_MAX_CHIPS},
    /** Small hint above the suggestions while the field is empty; '' hides it */
    suggestionHint: {type: String, default: 'Vorschläge – tippen, um zu suchen'},
    /** Shows a "Neu anlegen" row in the empty menu; emits `create` with the typed text */
    createText: {type: String, default: ''},
  },
  data: () => ({
    currentSearch: '' as string | null,
    pinnedKeys: [] as string[],
    menuActive: false,
  }),
  computed: {
    isReadonly(): boolean {
      return isOn(this.$attrs, 'readonly');
    },
    hasSelection(): boolean {
      return selectedKeys(this.value).length > 0;
    },
    /** Vuetify caches header objects with cache-items, so groups are only used without it */
    cachesItems(): boolean {
      return isOn(this.$attrs, 'cache-items') || isOn(this.$attrs, 'cacheItems');
    },
    displayItems(): unknown[] {
      const keyOf = (item: unknown) => itemKey(item, this.itemValue, this.itemText);
      return buildMenuItems(this.items || [], {
        pinnedKeys: this.pinSelected ? this.pinnedKeys : [],
        keyOf,
        groupOf: this.cachesItems ? undefined : this.groupBy,
        groupOrder: this.groupOrder,
      });
    },
    computedMenuProps(): Record<string, unknown> {
      return mergeMenuProps(this.menuProps);
    },
    computedClearable(): boolean {
      return this.clearable || (this.multiple && this.hasSelection && !this.disabled && !this.isReadonly);
    },
    empty(): EmptyState {
      return emptyState(this.currentSearch, !!this.loading, this.noDataText);
    },
    showHint(): boolean {
      return showSuggestionHint(this.currentSearch, this.hasSelection, (this.items || []).length, this.suggestionHint);
    },
    rootClass(): Record<string, boolean> {
      return {'soft-ac': true, 'soft-ac--multiple': this.multiple, 'soft-ac--has-chips': this.multiple && this.hasSelection};
    },
  },
  methods: {
    hasSlot(name: string): boolean {
      return !!(this.$scopedSlots[name] || this.$slots[name]);
    },
    /** Plain slots of the wrapping component (Vuetify reads e.g. append-item / append-outer from $slots) */
    passedSlotNames(): string[] {
      return Object.keys(this.$slots).filter((name) => name !== 'default');
    },
    passedScopedSlotNames(): string[] {
      return Object.keys(this.$scopedSlots).filter((name) => !this.$slots[name] && name !== 'default');
    },
    textOf(item: unknown): string {
      if (item !== null && typeof item === 'object') {
        const value = (item as Record<string, unknown>)[this.itemText];
        return value === undefined || value === null ? '' : String(value);
      }
      return item === undefined || item === null ? '' : String(item);
    },
    chipColor(item: unknown): string {
      const record = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      return cssColor(record.avatarColor) || cssColor(record.color);
    },
    chipAt(index: number, parent: SelectParent): ChipSlot {
      return chipSlot(index, parent.selectedItems.length, this.maxChips);
    },
    hiddenTitle(parent: SelectParent): string {
      return hiddenSelectionTitle(parent.selectedItems.map((item) => this.textOf(item)), this.maxChips);
    },
    onSearch(search: string | null) {
      this.currentSearch = search;
    },
    pinSelection() {
      this.pinnedKeys = selectedKeys(this.value);
    },
  },
  watch: {
    searchInput: {
      immediate: true,
      handler(search: string | undefined) {
        if (search !== undefined) {
          this.currentSearch = search;
        }
      },
    },
    value: {
      immediate: true,
      handler() {
        // While the menu is open the order stays as it was when it opened
        if (!this.menuActive) {
          this.pinSelection();
        }
      },
    },
  },
  mounted() {
    this.$watch(() => (this.$refs.ac as unknown as { isMenuActive?: boolean } | undefined)?.isMenuActive, (active?: boolean) => {
      this.menuActive = !!active;
      this.pinSelection();
    });
  },
});
</script>

<style src="./autocomplete.css"></style>
